import {
  EVENING_PART_LIST_CODE,
  formatPlanStartTime,
  type AppSettingList,
  type SettingListOption,
} from '@peles/shared';
import { readSettingList } from './settingLists';

/** What a plan says about the part of the evening it lays out. */
export interface PlanPartFields {
  eveningPart?: string | null;
  startTime?: string | null;
}

/** The office's parts of the evening, from the settings lists. */
export function readEveningParts(lists: readonly AppSettingList[]): SettingListOption[] {
  return readSettingList(lists, EVENING_PART_LIST_CODE);
}

/** A part's name as Settings has it — or its stored value, once Settings no longer has it. */
export function eveningPartLabel(
  value: string | null | undefined,
  parts: readonly SettingListOption[],
): string | null {
  if (!value) return null;
  return parts.find((p) => p.value === value)?.label ?? value;
}

/** A plan's part and its time — "Dinner · 7:30 PM" — or null when it has neither. */
export function describePlanPart(
  plan: PlanPartFields,
  parts: readonly SettingListOption[],
): string | null {
  const label = eveningPartLabel(plan.eveningPart, parts);
  const time = formatPlanStartTime(plan.startTime);
  if (label && time) return `${label} · ${time}`;
  return label ?? time;
}

/** Whether any of an event's plans is for a part of the evening. */
export function hasPlanParts(plans: readonly PlanPartFields[]): boolean {
  return plans.some((p) => !!p.eveningPart || !!formatPlanStartTime(p.startTime));
}

/** The first part Settings lists that none of the event's plans is for yet. */
export function nextEveningPart(
  plans: readonly PlanPartFields[],
  parts: readonly SettingListOption[],
): SettingListOption | null {
  const used = new Set(plans.map((p) => p.eveningPart).filter(Boolean));
  return parts.find((p) => !used.has(p.value)) ?? null;
}
